import React, { FC, useRef, useState } from 'react';

import {
  Stack,
  HStack,
  Box,
  Link,
  Text,
  useDisclosure,
} from '@chakra-ui/react';

import {
  DetailsIdentifier,
  EllipsisText,
  ExpandableText,
  renderSeverityBar,
  timeLabel,
} from 'components/DataDisplay';
import { CVEAdditionalDetailsModal } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/CVE/CVEAdditionalDetailsModal';
import { CVEReferencesModal } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/CVE/CVEReferencesModal';
import {
  renderCVEPackageName,
  renderCVSSScore,
} from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/CVE/utils';
import {
  renderCVEInformationButton,
  renderCVEInsights,
} from 'containers/Visibility/Vulnerabilities/Components/VulnerabilityTable/utils';
import { VulnerabilityViewByTabs } from 'containers/Visibility/Vulnerabilities/types';
import { limitedString } from 'utils/string';

interface Props {
  row: Record<string, any>;
  selectedTab: string;
}

export const CVEHostsDrawerDetails: FC<Props> = props => {
  const { row, selectedTab } = props;
  const cveInfoDisclosure = useDisclosure();
  const [showMoreLinks, setShowMoreLinks] = useState<boolean>(false);

  const ref = useRef(document.querySelector('.portal-container'));

  return (
    <>
      <HStack align="flex-start" spacing={10}>
        <Stack w={'70%'} overflow="hidden">
          <Box minH={40}>
            <DetailsIdentifier
              label={'Description'}
              value={
                <ExpandableText noOfLines={5}>
                  {row?.description}
                </ExpandableText>
              }
              isInline={false}
            />
          </Box>
          <HStack pt={3} align="flex-start">
            <Stack w="33%" spacing={3}>
              <DetailsIdentifier
                label={
                  'Package' +
                  (selectedTab === VulnerabilityViewByTabs.CVE ? '(s)' : '')
                }
                value={renderCVEPackageName(row)}
                isInline={false}
              />
              <DetailsIdentifier
                label="CVSS Score"
                value={
                  <HStack>
                    {renderCVSSScore(row)}
                    {renderCVEInformationButton(cveInfoDisclosure.onOpen)}
                  </HStack>
                }
                isInline={false}
              />
            </Stack>
            <Stack w="33%" spacing={3}>
              <DetailsIdentifier
                label={
                  'Version' +
                  (selectedTab === VulnerabilityViewByTabs.Package ? '' : '(s)')
                }
                value={
                  <EllipsisText>{row?.installed_version ?? '-'}</EllipsisText>
                }
                isInline={false}
              />
              <DetailsIdentifier
                label="Source"
                value={
                  row?.data_source ? (
                    row?.data_source?.URL ? (
                      <Link href={row?.data_source?.URL} target="_blank">
                        <Text color="primary" cursor="pointer" noOfLines={1}>
                          {limitedString(row?.data_source?.Name)}
                        </Text>
                      </Link>
                    ) : (
                      <EllipsisText>{row?.data_source?.source}</EllipsisText>
                    )
                  ) : (
                    '-'
                  )
                }
                isInline={false}
              />
            </Stack>
            <Stack w="33%" spacing={3}>
              <DetailsIdentifier
                label="Patch Version(s)"
                value={
                  <Text textOverflow="ellipsis" whiteSpace="nowrap">
                    {row?.fixed_in_version ?? '-'}
                  </Text>
                }
                isInline={false}
              />
            </Stack>
          </HStack>
        </Stack>
        <Stack spacing={3} w="30%">
          <DetailsIdentifier
            label="Severity"
            value={renderSeverityBar({
              value: row?.severity,
            })}
            isInline={false}
          />
          <DetailsIdentifier
            label="Published"
            value={timeLabel(row?.published_date)}
            isInline={false}
          />
          <DetailsIdentifier
            label="Updated"
            value={timeLabel(row?.last_modified_date)}
            isInline={false}
          />
          <DetailsIdentifier
            label="Insights"
            value={renderCVEInsights(row)}
            isInline={false}
          />
        </Stack>
      </HStack>
      {showMoreLinks && (
        <CVEReferencesModal
          isOpen={showMoreLinks}
          onClose={() => setShowMoreLinks(false)}
          cveDetails={row}
        />
      )}
      {cveInfoDisclosure.isOpen && (
        <CVEAdditionalDetailsModal
          cveDetails={row}
          isOpen={cveInfoDisclosure.isOpen}
          onClose={cveInfoDisclosure.onClose}
        />
      )}
    </>
  );
};
